import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api, { apiError } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid, Legend,
} from 'recharts';
import {
  Eye, Users, UserCheck, UserX, Globe, Monitor, Smartphone, Tablet, BarChart3, Shield,
} from 'lucide-react';
import { StatCard, useAdminFmt } from '@/components/AdminShared';
import { toast } from 'sonner';

const DEVICE_ICON = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };

export const AdminTraffic = () => {
  const { t } = useTranslation();
  const { locale, fmtDateTime } = useAdminFmt();
  const [days, setDays] = useState('30');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (d) => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/traffic?days=${d}`);
      setData(res.data);
    } catch (e) {
      toast.error(apiError(e, t('traffic.loadError')));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(days); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [days]);

  if (loading && !data) {
    return (
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
      </div>
    );
  }
  if (!data) return null;

  const tot = data.totals || {};
  const chartData = (data.timeseries || []).map((p) => ({
    ...p,
    label: new Date(p.date).toLocaleDateString(locale, { day: 'numeric', month: 'short' }),
  }));
  const maxBar = Math.max(1, ...(data.top_pages || []).map((p) => p.views));
  const noData = (tot.views || 0) === 0;

  return (
    <div className="space-y-6" data-testid="admin-traffic">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-xl flex items-center gap-2"><BarChart3 className="w-5 h-5 text-[#00B4D8]" /> {t('traffic.title')}</h3>
          <p className="text-sm text-muted-foreground mt-0.5">{t('traffic.subtitle')}</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-44" data-testid="traffic-range-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">{t('traffic.range', { n: 7 })}</SelectItem>
            <SelectItem value="30">{t('traffic.range', { n: 30 })}</SelectItem>
            <SelectItem value="90">{t('traffic.range', { n: 90 })}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-start gap-2 rounded-xl border border-[#2EE59D]/25 bg-[#2EE59D]/8 px-4 py-3">
        <Shield className="w-4 h-4 text-[#2EE59D] mt-0.5 shrink-0" />
        <p className="text-xs text-white/80" data-testid="traffic-privacy-note">{t('traffic.privacyNote')}</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Eye} label={t('traffic.views')} value={tot.views ?? 0} accent="#00B4D8" testid="traffic-stat-views" />
        <StatCard icon={Users} label={t('traffic.uniqueVisitors')} value={tot.unique_visitors ?? 0} accent="#48CAE4" testid="traffic-stat-unique" />
        <StatCard icon={UserCheck} label={t('traffic.authViews')} value={tot.auth_views ?? 0} accent="#2EE59D" testid="traffic-stat-auth" />
        <StatCard icon={UserX} label={t('traffic.anonViews')} value={tot.anon_views ?? 0} accent="#FFB020" testid="traffic-stat-anon" />
      </div>

      {noData ? (
        <div className="kite-card p-10 text-center text-muted-foreground" data-testid="traffic-empty">
          <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>{t('traffic.empty')}</p>
        </div>
      ) : (
        <>
          <div className="kite-card p-5">
            <h4 className="label-eyebrow text-muted-foreground mb-4">{t('traffic.dailyTitle')}</h4>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                <defs>
                  <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00B4D8" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#00B4D8" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gUnique" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2EE59D" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#2EE59D" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="label" tick={{ fill: '#8B9398', fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis tick={{ fill: '#8B9398', fontSize: 11 }} allowDecimals={false} />
                <RTooltip contentStyle={{ background: '#12161B', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 12, color: '#fff' }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="views" name={t('traffic.viewsLabel')} stroke="#00B4D8" fill="url(#gViews)" strokeWidth={2} />
                <Area type="monotone" dataKey="unique" name={t('traffic.uniqueLabel')} stroke="#2EE59D" fill="url(#gUnique)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="grid lg:grid-cols-2 gap-5">
            <div className="kite-card p-5">
              <h4 className="label-eyebrow text-muted-foreground mb-4">{t('traffic.topPages')}</h4>
              <div className="space-y-2.5" data-testid="traffic-top-pages">
                {(data.top_pages || []).map((p) => (
                  <div key={p.path} className="flex items-center gap-3">
                    <span className="font-data text-sm text-white/90 w-44 truncate" title={p.path}>{p.path}</span>
                    <div className="flex-1 h-2.5 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full rounded-full bg-[#00B4D8]" style={{ width: `${Math.max(4, (p.views / maxBar) * 100)}%` }} />
                    </div>
                    <span className="font-data text-sm text-white/70 w-10 text-right">{p.views}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="kite-card p-5">
              <h4 className="label-eyebrow text-muted-foreground mb-4">{t('traffic.referrers')}</h4>
              <div className="space-y-2.5" data-testid="traffic-referrers">
                {(data.referrers || []).map((r) => (
                  <div key={r.source} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-white/85"><Globe className="w-3.5 h-3.5 text-[#48CAE4]" /> {r.source === 'direct' ? t('traffic.direct') : r.source === 'internal' ? t('traffic.internal') : r.source}</span>
                    <span className="font-data text-white/70">{r.views}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-5">
            <div className="kite-card p-5">
              <h4 className="label-eyebrow text-muted-foreground mb-4">{t('traffic.devices')}</h4>
              <div className="space-y-2.5" data-testid="traffic-devices">
                {(data.devices || []).map((d) => {
                  const Icon = DEVICE_ICON[d.name] || Monitor;
                  return (
                    <div key={d.name} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-white/85"><Icon className="w-3.5 h-3.5 text-[#48CAE4]" /> {t(`traffic.device.${d.name}`, { defaultValue: d.name })}</span>
                      <span className="font-data text-white/70">{d.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="kite-card p-5">
              <h4 className="label-eyebrow text-muted-foreground mb-4">{t('traffic.browsers')}</h4>
              <div className="space-y-2.5" data-testid="traffic-browsers">
                {(data.browsers || []).map((b) => (
                  <div key={b.name} className="flex items-center justify-between text-sm">
                    <span className="text-white/85">{b.name}</span>
                    <span className="font-data text-white/70">{b.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="kite-card p-5">
            <h4 className="label-eyebrow text-muted-foreground mb-4 flex items-center gap-2"><UserCheck className="w-4 h-4 text-[#2EE59D]" /> {t('traffic.perUser')}</h4>
            {(data.per_user || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('traffic.perUserEmpty')}</p>
            ) : (
              <Table data-testid="traffic-per-user">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('traffic.user')}</TableHead>
                    <TableHead className="text-right">{t('traffic.views')}</TableHead>
                    <TableHead className="text-right">{t('traffic.pagesCount')}</TableHead>
                    <TableHead className="text-right">{t('traffic.lastSeen')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.per_user.map((u) => (
                    <TableRow key={u.user_id}>
                      <TableCell>
                        <div className="font-medium text-white">{u.name}</div>
                        <div className="text-xs text-muted-foreground">{u.email}</div>
                      </TableCell>
                      <TableCell className="text-right font-data">{u.views}</TableCell>
                      <TableCell className="text-right font-data">{u.pages}</TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">{fmtDateTime(u.last_seen)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </>
      )}
    </div>
  );
};
